import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyL, BodyM } from '@sorare/core/src/atoms/typography';
import {
  COVERAGE,
  FOOTBALL_LEAGUES_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { flagUrl } from '@sorare/core/src/lib/territories';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FootballTabs } from '#src/LicensedPartners/PartnersList/FootballTabs/index.tsx';
import { Header } from '#src/LicensedPartners/PartnersList/Header/index.tsx';
import { Item } from '#src/LicensedPartners/PartnersList/Item/index.tsx';
import { StickySectionTitle } from '#src/LicensedPartners/PartnersList/StickySectionTitle/index.tsx';

import type {
  FootballCompetitionQuery,
  FootballCompetitionQueryVariables,
} from './__generated__/index.graphql.ts';
import { sortCompetition } from './sortCompetition.ts';

const LeagueName = styled(BodyL)`
  white-space: nowrap;
`;
const CompetitionsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding-top: var(--double-unit);
  @media ${laptopAndAbove} {
    grid-template-columns: repeat(4, 1fr);
    margin-left: 270px;
    margin-top: -100px;
    padding-top: 0;
  }
  margin-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
`;
const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: var(--quadruple-unit);
  background-color: var(--c-nd-50);
  gap: var(--double-unit);
  padding: var(--unit) var(--double-unit);
  &:hover,
  &:focus {
    color: inherit;
  }
  @media ${laptopAndAbove} {
    margin-left: 270px;
  }
`;

const FOOTBALL_COMPETITIONS_QUERY = gql`
  query FootballCompetitionQuery {
    football {
      leaguesOpenForGameStats {
        slug
        displayName
        pictureUrl
        released
        country {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<
  FootballCompetitionQuery,
  FootballCompetitionQueryVariables
>;

export const FootballCompetitions = () => {
  const [searchValue, setSearchValue] = useState('');

  const { formatMessage } = useIntl();

  const { data, loading } = useQuery(FOOTBALL_COMPETITIONS_QUERY);

  const competitions = data?.football?.leaguesOpenForGameStats;

  const competitionsToDisplay = useMemo(() => {
    // Filtering out russian competitions because of the war in Ukraine
    const licensedCompetitionsWithoutRussians = competitions?.filter(
      competition => competition.released && competition.country?.slug !== 'ru'
    );
    if (searchValue.trim()) {
      return licensedCompetitionsWithoutRussians
        ?.filter(
          c =>
            c.displayName.toLowerCase().includes(searchValue.toLowerCase()) ||
            c.slug.includes(searchValue.toLowerCase())
        )
        .sort(sortCompetition);
    }
    return [...(licensedCompetitionsWithoutRussians || [])].sort(
      sortCompetition
    );
  }, [competitions, searchValue]);

  return (
    <>
      <Header
        onSearchInputChange={setSearchValue}
        searchPlaceholder={formatMessage({
          id: 'LicensedClubs.footballLeagueSearch',
          defaultMessage: 'Search for a league',
        })}
      >
        <FootballTabs />
      </Header>
      <StickySectionTitle href="#competition">
        <LeagueName>
          <FormattedMessage
            id="LicensedClubs.FootballCompetitions.licensedLeagues"
            defaultMessage="Licensed Leagues"
          />
        </LeagueName>
      </StickySectionTitle>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <CompetitionsList id="competition">
            {competitionsToDisplay?.map(competition => {
              return (
                <Item
                  key={competition.slug}
                  name={competition.displayName}
                  to={generatePath(FOOTBALL_LEAGUES_SLUG, {
                    slug: competition.slug,
                  })}
                  logoUrl={
                    competition.pictureUrl
                      ? competition.pictureUrl
                      : flagUrl({ country: competition.country })
                  }
                />
              );
            })}
          </CompetitionsList>
          <StyledLink to={COVERAGE}>
            <BodyM>
              <FormattedMessage
                id="LicensedClubs.FootballCompetitions.moreLeagues"
                defaultMessage="Discover more covered competitions"
              />
            </BodyM>

            <Button size="small" color="tertiary" asSpan>
              <FormattedMessage
                id="LicensedClubs.FootballCompetitions.coverage"
                defaultMessage="Coverage"
              />
            </Button>
          </StyledLink>
        </>
      )}
    </>
  );
};
